import { useContext, useEffect, useState } from "react";
import { AppContext } from "contexts/AppContext";
import SectionWrapper from "components/layouts/SectionWrapper";
import { Box, Grid2, Skeleton, useMediaQuery } from "@mui/material";
import Headline6 from "components/elements/Headline6";
import { useNavigate } from "react-router-dom";
import { callAPI } from "utils/API";
import Headline3 from "components/elements/Headline3";
import ContentNormal from "components/elements/ContentNormal";
import Headline5 from "components/elements/Headline5";
import ButtonMedium4 from "components/elements/ButtonMedium4";
import ArrowRightAlt from "components/icons/ArrowRightAlt";
import { shuffle } from "./util";
import HorizontalScrollBlock from "components/blocks/horizontalScrollBlock/HorizontalScrollBlock";

const CategorySection = () => {
    const navigate = useNavigate()
    const isMobile = useMediaQuery('(max-width: 1200px)');
    const [categories, setCategories] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const { accessibleShops, styleGuide, brandData } = useContext(AppContext);
    const ShopID = accessibleShops && accessibleShops.length > 0 ? accessibleShops[0].ShopID : null
    const { frontPageCategorySectionBackgroundColor, frontPageCategorySectionTextColor } = brandData && brandData.features
    const backgroundColor = frontPageCategorySectionBackgroundColor || styleGuide.color3
    const textColor = frontPageCategorySectionTextColor || styleGuide.color7

    const fetchCategories = async () => {
        const fetchedCategories = await callAPI(`/product/${ShopID}/categories`, "GET")
        if (fetchedCategories && fetchedCategories.length > 0) {
            // Fetch user's preferred categories
            const fetchedPreferredCategoryIds = await callAPI(`/user/getPreferredCategoryIds`, "GET")
            const categoriesToShow = fetchedCategories.filter(c => !!c.imageURL && !!c.categoryDescription)
            const preferredCategories = categoriesToShow.filter(c => fetchedPreferredCategoryIds.some(cid => cid === c.CategoryID))
            const otherCategories = categoriesToShow.filter(c => !fetchedPreferredCategoryIds.some(cid => cid === c.CategoryID))
            
            // Shuffle order of categories but preferred first
            shuffle(preferredCategories)
            shuffle(otherCategories)
            setCategories([...preferredCategories, ...otherCategories])
        }
        setIsLoading(false)
    }

    useEffect(() => {
        if(ShopID) {
            fetchCategories()
        }
    }, [ShopID])

    const displayAmount = 4
    if(!isLoading && categories.length < displayAmount) {
        return null
    }

    const preferencesButton = <ButtonMedium4
        showIcon={false}
        translationKey="_HOME_UPDATE_PREFERENCES_"
        fallbackText="Opdatér dine præferencer"
        onClick={() => navigate(`/profile/settings`)}
    />
    
    const colSize = { xs: 3 }
    return (
        <SectionWrapper backgroundColor={backgroundColor}>
            <Box display={'flex'} flexDirection={'column'} alignItems={'center'} gap={2} paddingY={{xs: 6, md: 10}}>
                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} width={'100%'}>
                    {isLoading
                        ?
                        <Box flex={1} display={'flex'} flexDirection={'column'}>
                            <Skeleton width={200} />
                            <Skeleton width={260} height={42} />
                        </Box>
                        :
                        <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                            <Headline6
                                style={{ color: textColor }}
                                translationKey="_HOME_PAGE_CATEGORIES_HEADLINE_1_"
                                fallbackText="Kategorier"
                            />
                            <Headline3
                                style={{ color: textColor }}
                                translationKey="_HOME_PAGE_CATEGORIES_HEADLINE_2_"
                                fallbackText="Anbefalet til dig"
                            />
                        </Box>
                    }
                    {!isMobile && !isLoading && preferencesButton}
                </Box>
                {isMobile
                    ?
                    <Box marginTop={-2}>
                        <HorizontalScrollBlock>
                            {categories.map(c => (
                                <Box maxWidth={300}>
                                    <Category key={c.CategoryID} category={c} ShopID={ShopID} isLoading={isLoading} />
                                </Box>
                            ))}
                        </HorizontalScrollBlock>
                    </Box>
                    :
                    <Grid2 container spacing={'2%'} rowGap={4} width={'100%'}>
                        <Grid2 size={colSize}>
                            <Category category={categories[0]} ShopID={ShopID} isLoading={isLoading} />
                        </Grid2>
                        <Grid2 size={colSize}>
                            <Category category={categories[1]} ShopID={ShopID} isLoading={isLoading} />
                        </Grid2>
                        <Grid2 size={colSize}>
                            <Category category={categories[2]} ShopID={ShopID} isLoading={isLoading} />
                        </Grid2>
                        <Grid2 size={colSize}>
                            <Category category={categories[3]} ShopID={ShopID} isLoading={isLoading} />
                        </Grid2>
                    </Grid2>
                }
                {isMobile && !isLoading &&
                    <Box marginTop={2}>
                        {preferencesButton}
                    </Box>
                }
            </Box>
        </SectionWrapper>
    )
}

const Category = (props) => {
    const navigate = useNavigate()
    const isMobile = useMediaQuery('(max-width: 900px)');
    const { styleGuide, brandData } = useContext(AppContext)
    const [hover, setHover] = useState(false);
    const { frontPageCategorySectionTextColor } = brandData && brandData.features

    const toggleHover = (val) => {
        setHover(val);
    }

    const navigationLink = `/shop/${props.ShopID}?category=${encodeURIComponent(props.category?.categoryName)}`
    const textColor = frontPageCategorySectionTextColor || styleGuide.color7
    
    return (
        <Box
            onClick={() => navigate(navigationLink)}
            onMouseEnter={() => toggleHover(true)}
            onMouseLeave={() => toggleHover(false)}
            gap={2}
            flex={1}
            display={'flex'}
            minWidth={150}
            flexDirection={'column'}
            overflow={'hidden'}
            style={{cursor: 'pointer'}}
        >
            {props.isLoading
                ?
                    <Box width={'100%'} style={{ aspectRatio: 4 / 5 }}>
                        <Skeleton variant="rectangular" width={'100%'} height={'100%'}  />
                    </Box> 
                :
                    <img src={props.category.imageURL} style={{objectFit: 'cover', aspectRatio: 4/5, overflow: 'hidden' }} />
            }

            {props.isLoading
                ?
                    <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                        <Skeleton width={'90%'} />
                        <Skeleton width={'60%'} height={30} />
                    </Box>
                :
                    <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
                        <ContentNormal
                            style={{fontSize: {xs: 14, md: styleGuide.contentNormalFontSize}, color: textColor}}
                            fallbackText={props.category.categoryDescription || ' '}
                        />
                        <Box display={'flex'} alignItems={'center'} gap={hover ? 4 : 2} style={{transition: 'all .3s ease-in-out'}}>
                            <Headline5
                                style={{fontSize: {xs: 16, md: styleGuide.headline5FontSize}, color: textColor}}
                                fallbackText={props.category.categoryName || ' '}
                            />
                            {!isMobile && <ArrowRightAlt fill={textColor} />}
                        </Box>
                    </Box>
            }
            
        </Box>
    )
}


export default CategorySection